import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Freeze content at expanded width during the open/close animation (PANEL_ANIMATION_MS matches app.css [data-panel]).
const PANEL_ANIMATION_MS = 300;
const SETTLE_BUFFER_MS = 50;

export const usePanelRevealWidth = (isCollapsed: boolean) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const expandedWidth = useRef<number>();
  const lastToggle = useRef(0);
  const [frozenWidth, setFrozenWidth] = useState<number>();
  const prevCollapsed = useRef(isCollapsed);

  // useLayoutEffect: freeze before paint, else the first frame flickers squeezed.
  useLayoutEffect(() => {
    // Only when isCollapsed actually flips (skip mount + StrictMode double-invoke).
    if (prevCollapsed.current === isCollapsed) return;
    prevCollapsed.current = isCollapsed;
    // Skip drag-resizes (no width animation), else freezing pins the wrong width.
    const group = measureRef.current?.closest('[data-panel-group]');
    if (group?.querySelector('[data-resize-handle-state="drag"]')) return;
    lastToggle.current = performance.now();
    setFrozenWidth(expandedWidth.current);
    const timeout = setTimeout(() => setFrozenWidth(undefined), PANEL_ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [isCollapsed]);

  // Track the settled expanded width, skipping the animation window (else captures the in-flight width).
  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const update = () => {
      const settled =
        performance.now() - lastToggle.current > PANEL_ANIMATION_MS + SETTLE_BUFFER_MS;
      if (!isCollapsed && settled) expandedWidth.current = el.clientWidth;
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isCollapsed]);

  return { measureRef, frozenWidth };
};
